// index.js
import 'babel-polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { SolrClient } from './solr-faceted-search-react/src/index';
import FederatedSolrComponentPack from './components/federated_solr_component_pack';
import FederatedSolrFacetedSearch from './components/federated-solr-faceted-search';
import helpers from './helpers';
import reactTInit from './components/reactT/reactTInit';
import reactT from './components/reactT';
// import search app boilerplate styles
import './styles.css';

/**
 * Executes search query based on the value of URL querystring params.
 *
 * @param solrClient
 *   Instantiated solrClient.
 * @param options
 *   Config options, used to determine initial site search name
 */
const searchFromQuerystring = (solrClient, options = {}) => {
  // Get existing querystring params.
  const { parsed, params } = helpers.qs.getParsedQsAndParams();
  let searchFieldsState = solrClient.state.query.searchFields;

  // Set the state for searchFields based on qs params.
  searchFieldsState.forEach((searchField) => {
    // Get the field machine name for the main query field.
    if (Object.prototype.hasOwnProperty.call(options,'mainQueryField') && searchField.field === options.mainQueryField) {
      // Set the state of the main query field to the value of the search qs param
      searchField.value = parsed.keywords;
    }
    // If the searchField is one for which we preserve state through qs.
    if (helpers.filterFieldsWithQsState.find((filterField) => filterField === searchField.field )) {
      searchField = helpers.qs.setFieldStateFromQs({
        params,
        searchField
      }) // this resets our initial state of search sites
    }
    // If restricted to the current site by configuration, enforce it here.
    // This rule only applies if site has not been selected by the user.
    if (options.siteSearch !== undefined && searchField.field === 'sm_site_name' && searchField.value === undefined && searchField.isHidden === false) {
      searchField.value = [options.siteSearch];
    }
  });
  // Ensure the initial query succeeds by setting a default start value.
  if (parsed['page'] !== undefined && !isNaN(parsed['page']) && parseInt(parsed['page']) > 0) {
    solrClient.state.query.start = (parsed['page'] - 1) * 10;
  } else {
    solrClient.state.query.start = solrClient.state.query.start || 0;
  }

  // Send query based on state derived from querystring.
  solrClient.sendQuery(solrClient.state.query);
};

// Initialize the solr client + search app with settings.
const init = (settings) => {
  reactTInit();
  const langcode = document.querySelector('html').getAttribute('lang');
  const queryParams = new URLSearchParams(window.location.search)
  let keywordsValue;

  if (queryParams.get("keywords") && queryParams.get("keywords").length > 0) {
    keywordsValue = queryParams.get("keywords");
  }
  else {
    keywordsValue = '';
  }

  const defaults = {
    isD7: false,
    // Whether or not we should be querying the solr backend directly.
    proxyIsDisabled: false,
    // The query request endpoint url must be assigned in ./.env.local.js and by the search app settings in the module.
    url: "",
    // The search fields and filterable facets.
    searchFields: [
      {label: reactT('Enter Search Term') + ':', field: '*', type: "text", isHidden: false, value: keywordsValue ,id: "textSearchField"},
      {label: reactT('Site name'), field: "sm_site_name", type: "list-facet", isHidden: false, collapse: false},
      {label: reactT('Collections'), field: "ss_collection_name", type: "list-facet", isHidden: false, collapse: false, id: 'collection', showAllTitle: reactT('Collections')},
      {label: reactT('Country'), field: "sm_country_name", type: "list-facet", isHidden: false, collapse: false, id: 'countries', showAllTitle: reactT('Countries')},
      {label: reactT('Country classifications'), field: "sm_country_classification_name", type: "list-facet", isHidden: false, collapse: false, id: 'country_classifications',showAllTitle: reactT('Country classifications')},
      {label: reactT('Document source'), field: "sm_document_source_name", type: "list-facet", isHidden: false, collapse: false ,id: 'document_source', showAllTitle: reactT('Document sources')},
      {label: reactT('Document source classifications'), field: "sm_document_source_category", type: "list-facet", isHidden: false, collapse: false ,id: 'document_source_classifications', showAllTitle: reactT('Document source classifications')},
      {label: reactT('Document type'), field: "ss_document_type_name", type: "list-facet", isHidden: false, collapse: false, id: 'document_type', showAllTitle: reactT('Document types')},
      {label: reactT('Document subtype'), field: "sm_document_subtype_name", type: "list-facet", isHidden: false, collapse: false ,id: 'document_subtype', showAllTitle: reactT('Document subtypes')},
      {label: reactT('Judicial body'), field: "sm_judicial_body_name", type: "list-facet", isHidden: false, collapse: false, id: 'judicial_body', showAllTitle: reactT('Judicial bodies')},
      {label: reactT('Region'), field: "sm_region_name", type: "list-facet", isHidden: false, collapse: false, id: 'region',showAllTitle: reactT('Regions')},
      {label: reactT('Keywords'), field: "sm_keywords_name", type: "list-facet", isHidden: false, collapse: false, id: 'topic', showAllTitle: reactT('Keywords')},
      {label: reactT('Languages'), field: "sm_languages", type: "list-facet", isHidden: false, collapse: false, id: 'languages',showAllTitle: reactT('Languages')},
      {label: reactT('Legal instruments'), field: "sm_legal_instruments_name", type: "list-facet", isHidden: false, collapse: false, id: 'legal_instruments',showAllTitle: reactT('Legal instruments')},
      {label: reactT('Populations groups'), field: "sm_populations_groups_name", type: "list-facet", isHidden: false, collapse: false, id: 'population_group',showAllTitle: reactT('Populations groups')},
      {label: reactT('Religious groups'), field: "sm_religious_groups_name", type: "list-facet", isHidden: false, collapse: false, id: 'religious_group',showAllTitle: reactT('Religious groups')},
      {label: reactT('Protected persons'), field: "sm_persons_of_concern_name", type: "list-facet", isHidden: false, collapse: false, id: 'persons_of_concern',showAllTitle: reactT('Protected persons')},
      {label: reactT('Publication status'), field: "ss_document_status", type: "list-facet", isHidden: false, collapse: false, id: 'publication_status',showAllTitle: reactT('Publication status')},
      {label: reactT('Type'), field: "ss_type", type: "list-facet", isHidden: true},
      {label: reactT("Date"), field: "ds_created", type: "range-facet", isHidden: false, collapse: false},
      {label: reactT("Language"), field: "ss_search_api_language", type: "text", isHidden: true, value: [langcode]},
      {label: reactT('Protection manual'), field: "filter_pm", type: "text", isHidden: true},
    ],
    // The solr field to use as the source for the main query param "q".
    mainQueryField: '*',
    sortFields: [
      {label: reactT("Date (newest - oldest)"), field: "ds_created", order: 'desc'},
      {label: reactT("Date (oldest - newest)"), field: "ds_created", order: 'asc'},
      {label: reactT("Relevance"), field: "score", order: 'desc'},
      {label: reactT("Title (A-Z)"), field: "ss_title_string", order: 'asc'},
      {label: reactT("Title (Z-A)"), field: "ss_title_string", order: 'desc'},
    ],
    // Enable highlighting in search results snippets.
    hl: {
      fl: `tm_X3b_${langcode}_auto_aggregated_fulltext_field`, // the highlight snippet source field(s)
      usePhraseHighlighter: true // highlight phrase queries
    },
    pageStrategy: "paginate",
    rows: 10,
    // Hostname overridable in ./.env.local.js for testing purposes.
    hostname: window.location.hostname,
    autocomplete: false,
  };

  const options = Object.assign(defaults, settings);

  // Update searchFields to indicate which facets or filters should be hidden in the UI.
  // Note: these facets and filters may still be used in the query.
  settings.hiddenSearchFields = settings.hiddenSearchFields || [];
  options.searchFields = options.searchFields.sort(({label: a}, {label:b}) => a === "" ? -1 : (b === reactT('Enter Search Term') + ':' ? 1 : a.localeCompare(b))).map(searchField => {
    if (settings.hiddenSearchFields.includes(searchField.field)) {
      searchField.isHidden = true;
    }
    return searchField;
  });

  // Set sm_site_name default values from config
  const sm_site_name_value = settings.sm_site_name || false;

  // Logic for restricting site search based on config.
  options.siteList = [];
  if (settings.siteSearch !== undefined) {
    options.searchFields.forEach((searchField) => {
      if (searchField.field === 'sm_site_name' &&
        (searchField.isHidden || sm_site_name_value.length < 2)) {
        options.siteList = [settings.siteSearch];
      }
    });
  }
  else {
    options.siteList = sm_site_name_value;
  }

  // The client class.
  const solrClient = new SolrClient({
    isD7: options.isD7,
    proxyIsDisabled: options.proxyIsDisabled,
    url: options.url,
    userpass: options.userpass,
    searchFields: options.searchFields,
    sortFields: options.sortFields,
    pageStrategy: options.pageStrategy,
    rows: options.rows,
    hl: options.hl,
    mainQueryField: options.mainQueryField,
    filters: [{field: "sm_site_name", type: "list-facet", value: options.siteList}],
    // The change handler passes the current query- and result state for render
    // as well as the default handlers for interaction with the search component
    onChange: (state, handlers) =>
      // Render the faceted search component
      ReactDOM.render(
        <FederatedSolrFacetedSearch
          {...state}
          {...handlers}
          customComponents={FederatedSolrComponentPack}
          bootstrapCss={false}
          onSelectDoc={()=>{}}
          truncateFacetListsAt={-1}
          options={options}
        />,
        document.getElementById("fs-root")
      )
  });

  // Check if there is a querystring param search term and make initial query.
  searchFromQuerystring(solrClient, options);

  // Listen for browser history changes / updated querystring, make new query.
  // See https://developer.mozilla.org/en-US/docs/Web/Events/popstate
  window.onpopstate = function() {
    searchFromQuerystring(solrClient, options);
  };
};

// If we are in the production environment (i.e. using the build compiled js)
// @see https://github.com/facebook/create-react-app/blob/master/packages/react-scripts/template/README.md#adding-custom-environment-variables
if (process.env.NODE_ENV === 'production') {
  // Get the root element where the app will be rendered.
  const root = document.getElementById("fs-root");

  if (root) {
    // Get the data attribute which has the stringified configuration data json object.
    if (Object.hasOwnProperty.call(root.dataset, 'federatedSearchAppConfig')) {
      const settings = JSON.parse(root.dataset.federatedSearchAppConfig);
      init(settings);
    }
    else {
      console.error('Federated Search React | Could not find a data-federated-search-app-config attribute on div#root.  Please populate data-federated-search-app-config with search app configuration data.');
    }
  }
  else {
    console.error('Federated Search React | Could not find div#root in which to load the search app.');
  }
}
// This is not production (i.e. not using the build compiled js)
else {
  // Get the local environment settings for the search app and initialize.
  import('./.env.local.js')
    .then(
      (settings) => {
        init(settings); // Load the app, passing in the ./.env.local.js config.
      },
      (error) => {
        console.error('Federated Search React | Could not load local configuration for search app: ', error);
      }
    );
}
